/* eslint-disable no-nested-ternary */
import { Chip, Stack, Tooltip, Box, Typography } from "@mui/material";

// Utilitaires format
const fmtNb = (n) => (n === 0 || n ? new Intl.NumberFormat("fr-FR").format(n) : "");
const fmtMoney = (n) => (n === 0 || n ? `${fmtNb(n)} CFA` : "");
const fmtDate = (d) => {
  if (!d) return "";
  // d peut être Date, string ISO, Timestamp Firestore, etc.
  if (typeof d?.toDate === "function") d = d.toDate();
  if (typeof d?.seconds === "number") d = new Date(d.seconds * 1000);
  if (typeof d === "string") d = new Date(d);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric", month: "short", day: "2-digit"
  }).format(d);
};
const fmtRange = (min, max, unitFn = (v) => v) => {
  const a = min !== "" && min != null;
  const b = max !== "" && max != null;
  if (a && b) return `${unitFn(min)} – ${unitFn(max)}`;
  if (a) return `≥ ${unitFn(min)}`;
  if (b) return `≤ ${unitFn(max)}`;
  return "";
};
const boolTxt = (v) => (v === true ? "Oui" : v === false ? "Non" : "");

// Limiter l’affichage de longues listes (ex. événements/intérêts)
const listPreview = (arr = [], max = 2) => {
  if (!Array.isArray(arr) || arr.length === 0) return ["", 0];
  const shown = arr.slice(0, max);
  const more = Math.max(0, arr.length - shown.length);
  return [shown, more];
};

/**
 * events: [{id, title}] optionnel pour afficher des noms d’événements
 * types:  [{id, name}]  optionnel si tu veux mapper filterInterest à un label
 */
export function FilterChipsSummary({ filters, events = [], types = [] }) {
  if (!filters) return null;

  // Maps pour retrouver les labels
  const evMap = new Map(events.map((e) => [e.id, e.title || e.name || e.id]));
  const typeMap = new Map(types.map((t) => [t.id, t.data?.name || t.name || t.id]));

  const chips = [];

  // --- Événements
  if (filters?.filterEvent?.length) {
    const labels = filters.filterEvent.map((id) => evMap.get(id) || id);
    const [shown, more] = listPreview(labels);
    chips.push(
      <Tooltip key="evt" title={labels.join(" • ")}>
        <Chip size="small" label={`Événements: ${shown.join(", ")}${more ? ` +${more}` : ""}`} />
      </Tooltip>
    );
  }

  // --- Intérêts/genres (ou types)
  if (filters?.filterInterest?.length) {
    const labels = filters.filterInterest.map((id) => typeMap.get(id) || id);
    const [shown, more] = listPreview(labels);
    chips.push(
      <Tooltip key="int" title={labels.join(" • ")}>
        <Chip size="small" label={`Intérêts: ${shown.join(", ")}${more ? ` +${more}` : ""}`} />
      </Tooltip>
    );
  }

  // --- Nombre total d’évènements
  const nbEvt = fmtRange(filters?.filterNbEvent?.min, filters?.filterNbEvent?.max, fmtNb);
  if (nbEvt) chips.push(<Chip key="nbEvt" size="small" label={`Participations: ${nbEvt}`} />);

  // --- Nombre de billets
  const nbBillet = fmtRange(filters?.filterBillet?.min, filters?.filterBillet?.max, fmtNb);
  if (nbBillet) chips.push(<Chip key="billet" size="small" label={`Billets: ${nbBillet}`} />);

  // --- Total dépensé
  const dep = fmtRange(filters?.totalDepense?.min, filters?.totalDepense?.max, fmtMoney);
  if (dep) chips.push(<Chip key="dep" size="small" label={`Total dépensé: ${dep}`} />);

  // --- Dernier achat (date)
  const lastAfter = fmtDate(filters?.lastDateOrder?.after);
  const lastBefore = fmtDate(filters?.lastDateOrder?.before);
  let lastLbl = "";
  if (lastAfter && lastBefore) lastLbl = `${lastAfter} – ${lastBefore}`;
  else if (lastAfter) lastLbl = `Après ${lastAfter}`;
  else if (lastBefore) lastLbl = `Avant ${lastBefore}`;
  if (lastLbl) chips.push(<Chip key="last" size="small" label={`Dernier achat: ${lastLbl}`} />);

  // --- Abonnements / flags
  if (filters?.filterNewsletter != null) {
    chips.push(<Chip key="news" size="small" label={`Newsletter: ${boolTxt(filters.filterNewsletter)}`} />);
  }
  if (filters?.filterNotif != null) {
    chips.push(<Chip key="push" size="small" label={`Notifications push: ${boolTxt(filters.filterNotif)}`} />);
  }
  if (filters?.filterAbonne != null) {
    chips.push(<Chip key="page" size="small" label={`Abonné à la page: ${boolTxt(filters.filterAbonne)}`} />);
  }

  // --- Date d'ajout (historique)
  if (filters?.filterHist) {
    // si c’est un preset texte ("7j", "30j", etc.)
    chips.push(<Chip key="hist" size="small" label={`Ajout: ${filters.filterHist}`} />);
  }

  // --- Fenêtre temporelle globale (start/end)
  const start = fmtDate(filters?.start);
  const end = fmtDate(filters?.end);
  if (start || end) {
    const lbl = start && end ? `${start} – ${end}` : start ? `Depuis ${start}` : `Jusqu’à ${end}`;
    chips.push(<Chip key="window" size="small" label={`Période: ${lbl}`} />);
  }

  // Si aucun filtre effectif
  if (chips.length === 0) {
    chips.push(<Chip key="none" size="small" variant="outlined" label="Aucun filtre" />);
  }

  return (
    <Box mt={2}>
      <Typography variant="subtitle2">Résumé des filtres</Typography>
      <Typography variant="caption">
        (Aperçu des filtres appliqués à cette population)
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mt={1}>
        {chips}
      </Stack>
    </Box>
  );
}
